// Ghost Laundry — pricing engine. Single source of truth.
//
// Every price shown to the admin or to the customer runs through
// computeOrderTotal(). No other file computes a total. Ever.
//
// Contract (see docs/GHOST-ADMIN-SPEC.md §1):
//   - The service base is a FLAT charge covering 0 → included_lbs.
//     5 lbs and 15 lbs cost the same $45.
//   - 0 lb weighed ⇒ base = 0 (no order at all, no forfait).
//   - Overage is a computed consequence of the weight — never an order_items row.
//   - Add-ons multiply by their own unit (lb / bag / order / item).
//     `per_lb` add-ons re-price automatically when the weight changes.
//   - Handling percentage lives on services.handling_fee_pct (12.00), never hard-coded.
//   - All money is cents. Round once, at the end.

import {
  isPromoActive, isPromoShirt, PROMO_MIN_SHIRTS, FREE_SHIRT_REASON,
} from './promo'

/**
 * @param {object} args
 * @param {object} args.service - row from `services`
 *   { pricing_mode: 'per_pound' | 'per_item' | 'subscription',
 *     base_price_cents, included_lbs, extra_lb_cents,
 *     monthly_included_lbs, handling_fee_pct }
 * @param {number|null|undefined} args.weighedLbs
 * @param {number|string} args.bags
 * @param {Array} args.items - add-on rows already attached
 *   { unit_price_cents, unit: 'per_lb'|'per_bag'|'per_order'|'per_item', quantity, ...meta }
 * @param {number} [args.memberLbsUsedThisMonth]
 */
export function computeOrderTotal({
  service,
  weighedLbs,
  bags,
  items,
  memberLbsUsedThisMonth = 0,
  // Prepaid lbs currently available to this member. When present, they
  // absorb overage lbs first (spec §7ter Phase 3) — the invoice only
  // charges what spills past cycle AND prepaid. Non-members pass 0.
  prepaidLbsAvailable = 0,
  // Staff discount on the service portion (base + overage) — orders.wf_discount_*.
  // Line discounts ride on each item (discount_type / discount_value).
  serviceDiscount = null,
  // Injected so the promo window is testable without mocking the clock.
  // Defaults to now, which is what every real caller wants.
  now = Date.now(),
}) {
  const L = Number(weighedLbs) || 0
  const B = Number(bags) || 1
  const list = Array.isArray(items) ? items : []

  // ── 1. Service base + overage (never as order_items) ─────────────────
  let baseCents = 0
  let overageCents = 0
  let overageLbs = 0
  let prepaidLbsUsed = 0

  if (service?.pricing_mode === 'subscription') {
    const incl = Number(service.monthly_included_lbs) || 0
    const before = Number(memberLbsUsedThisMonth) || 0
    const after = before + L
    // Only the portion of the delta that crosses the monthly cap counts.
    const rawOverage = Math.max(0, after - incl) - Math.max(0, before - incl)
    // Prepaid lbs eat into the overage first.
    prepaidLbsUsed = Math.min(rawOverage, Math.max(0, Number(prepaidLbsAvailable) || 0))
    overageLbs = rawOverage - prepaidLbsUsed
    overageCents = Math.round(overageLbs * (Number(service.extra_lb_cents) || 0))
    baseCents = 0 // The $95/mo subscription is billed separately, not per-order.
  } else if (service?.pricing_mode === 'per_pound') {
    // Flat forfait — never included_lbs × extra_lb_cents.
    baseCents = L > 0 ? (Number(service.base_price_cents) || 0) : 0
    overageLbs = Math.max(0, L - (Number(service.included_lbs) || 0))
    overageCents = Math.round(overageLbs * (Number(service.extra_lb_cents) || 0))
  } // per_item (Dry Cleaning): base 0, everything runs through items.

  // ── 2. Add-ons: each multiplies by its own unit ──────────────────────
  const lines = list.map((it) => {
    const q = Number(it.quantity) || 0
    let multiplier
    switch (it.unit) {
      case 'per_lb':    multiplier = L;         break
      case 'per_bag':   multiplier = B * q;     break
      case 'per_order': multiplier = q > 0 ? 1 : 0; break
      case 'per_item':
      default:          multiplier = q;         break
    }
    const unitPrice = Number(it.unit_price_cents) || 0
    const lineGrossCents = Math.round(unitPrice * multiplier)
    const lineTotalCents = applyDiscount(lineGrossCents, it.discount_type, it.discount_value)
    return {
      ...it,
      multiplier,
      lineGrossCents,
      lineDiscountCents: lineGrossCents - lineTotalCents,
      lineTotalCents,
    }
  })
  // ── 2b. "3 shirts, the cheapest one is free" ─────────────────────────
  //
  // The engine always returns what the customer actually owes — including
  // on the booking screen, where no order_items row exists yet and the
  // promo is the whole reason they are booking.
  //
  // The double-charge hazard is handled by `applied`: if the chosen row
  // ALREADY carries the promo discount (it was saved earlier), the line
  // loop above has it covered and nothing more is taken off here. Deducting
  // in both places would give the shirt away twice.
  //
  // ⚠️ The discount still has to be WRITTEN to order_items by whoever
  // persists the order — see reconcileFreeShirt. A stored line_total_cents
  // that includes the promo, with no discount row behind it, is a bill the
  // invoice cannot explain. That is why the returned line carries
  // `freeShirtCents`: a persister copying `lineTotalCents` can see it owes
  // a discount row too.
  const freeShirt = findFreeShirt(lines, service, now)
  if (freeShirt) {
    const target = lines.find((l) => l.id === freeShirt.lineId)
    freeShirt.applied = target?.discount_reason !== FREE_SHIRT_REASON
    if (freeShirt.applied && target) {
      const off = Math.min(freeShirt.amountCents, target.lineTotalCents)
      target.lineTotalCents -= off
      target.lineDiscountCents += off
      target.freeShirtCents = off
      freeShirt.amountCents = off
    }
  }

  const itemsCents = lines.reduce((a, l) => a + l.lineTotalCents, 0)

  // ── 3. Service discount (base + overage) ─────────────────────────────
  const serviceGrossCents = baseCents + overageCents
  const serviceNetCents = applyDiscount(serviceGrossCents, serviceDiscount?.type, serviceDiscount?.value)
  const serviceDiscountCents = serviceGrossCents - serviceNetCents
  const discountCents = serviceDiscountCents + lines.reduce((a, l) => a + l.lineDiscountCents, 0)

  // ── 4. Subtotal → handling → total ───────────────────────────────────
  // Handling is charged on the discounted subtotal.
  const subtotalCents = serviceNetCents + itemsCents
  const handlingPct = Number(service?.handling_fee_pct) || 0
  const handlingCents = Math.round(subtotalCents * (handlingPct / 100))

  return {
    baseCents,
    overageCents,
    overageLbs,
    prepaidLbsUsed,
    serviceGrossCents,
    serviceDiscountCents,
    discountCents,
    itemsCents,
    lines,
    subtotalCents,
    handlingCents,
    totalCents: subtotalCents + handlingCents,
    // { lineId, amountCents, shirtCount } — or null. See findFreeShirt.
    freeShirt,
  }
}

/**
 * Which line, if any, should carry the free-shirt discount.
 *
 * Returns `{ lineId, amountCents, shirtCount }` or null. It reports; the
 * caller writes. Rules, in order:
 *
 *  - dry cleaning only (`per_item`), and only while the promo runs
 *  - at least PROMO_MIN_SHIRTS shirts across all shirt lines, quantities
 *    counted — 2 Dress shirts + 1 Polo is three shirts
 *  - one free shirt per order, however many they send
 *  - the CHEAPEST shirt is the free one, read from the line's own
 *    unit_price_cents: $8 in South Florida, $10 in Ponte Vedra, and the
 *    line is the only place that knows which
 *  - a line already carrying a MANUAL discount is skipped, never
 *    overwritten — Sébastien outranks the automation. A line carrying the
 *    promo's own discount stays eligible, otherwise re-saving a ticket
 *    would strip the discount it just applied.
 */
export function findFreeShirt(lines, service, now = Date.now()) {
  if (service?.pricing_mode !== 'per_item') return null
  if (!isPromoActive(now)) return null

  const shirts = lines.filter((l) => isPromoShirt(l.item_id))
  const shirtCount = shirts.reduce((n, l) => n + (Number(l.quantity) || 0), 0)
  if (shirtCount < PROMO_MIN_SHIRTS) return null

  const eligible = shirts.filter(
    (l) => !l.discount_type || l.discount_reason === FREE_SHIRT_REASON,
  )
  if (eligible.length === 0) return null

  const cheapest = eligible.reduce((best, l) =>
    (Number(l.unit_price_cents) || 0) < (Number(best.unit_price_cents) || 0) ? l : best,
  )
  const amountCents = Number(cheapest.unit_price_cents) || 0
  if (amountCents <= 0) return null

  return { lineId: cheapest.id, amountCents, shirtCount }
}

// Apply a staff discount to an amount in cents. `amount` values are dollars,
// `percent` is capped at 100. Never goes below 0.
export function applyDiscount(cents, type, value) {
  const c = Math.max(0, Math.round(Number(cents) || 0))
  const v = Math.max(0, Number(value) || 0)
  if (type === 'amount') return Math.max(0, c - Math.round(v * 100))
  if (type === 'percent') return Math.max(0, Math.round(c * (1 - Math.min(v, 100) / 100)))
  return c
}

// Short human label: "-$2.00" / "-10%".
export function discountLabel(type, value) {
  const v = Number(value) || 0
  if (type === 'amount') return `-$${v.toFixed(2)}`
  if (type === 'percent') return `-${+v.toFixed(2)}%`
  return ''
}

// Money formatter — kept exported so callers don't hand-roll it.
export function fmtMoney(cents) {
  if (cents == null || cents === '') return '—'
  return `$${(cents / 100).toFixed(2)}`
}
